import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const [langOpen, setLangOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);

  const languages = [
    { code: 'en', label: t('lang_en') },
    { code: 'de', label: t('lang_de') },
    { code: 'fr', label: t('lang_fr') },
    { code: 'da', label: t('lang_da') },
  ];

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white/80 backdrop-blur-md border-b border-background-200/50">
      <div className="w-full px-4 md:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-18">
          {/* Logo */}
          <a href="/" className="flex items-center gap-2 text-lg md:text-xl font-bold text-foreground-900 tracking-tight">
            <span className="text-primary-500">ACB</span>
            <span className="hidden sm:inline text-foreground-700 font-medium">Spreadsheet</span>
          </a>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-6">
            <a href="/" className="text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors">
              {t('nav_home')}
            </a>
            <div className="relative" ref={langRef}>
              <button
                onClick={() => setLangOpen(!langOpen)}
                className="flex items-center gap-1.5 text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors cursor-pointer"
              >
                <i className="ri-global-line w-4 h-4 flex items-center justify-center"></i>
                {t('language')}
                <i className={`ri-arrow-down-s-line w-4 h-4 flex items-center justify-center transition-transform ${langOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {langOpen && (
                <div className="absolute right-0 mt-2 w-40 bg-white rounded-lg shadow-lg border border-background-200/80 py-1 z-50">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => {
                        i18n.changeLanguage(lang.code);
                        setLangOpen(false);
                      }}
                      className={`w-full text-left px-4 py-2 text-sm transition-colors cursor-pointer ${
                        i18n.language === lang.code
                          ? 'text-primary-600 bg-primary-50/50 font-medium'
                          : 'text-foreground-700 hover:bg-background-100'
                      }`}
                    >
                      {lang.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-700 cursor-pointer"
          >
            <i className={`ri-${mobileOpen ? 'close' : 'menu'}-line text-xl`}></i>
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden bg-white border-t border-background-200/50 px-4 py-4 space-y-3">
          <a href="/" className="block text-sm font-medium text-foreground-700 py-2" onClick={() => setMobileOpen(false)}>
            {t('nav_home')}
          </a>
          <div className="border-t border-background-200/50 pt-3">
            <p className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-2">{t('language')}</p>
            <div className="space-y-1">
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => {
                    i18n.changeLanguage(lang.code);
                    setMobileOpen(false);
                  }}
                  className={`w-full text-left px-3 py-2 text-sm rounded-md transition-colors cursor-pointer ${
                    i18n.language === lang.code ? 'text-primary-600 bg-primary-50/50 font-medium' : 'text-foreground-700'
                  }`}
                >
                  {lang.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}